import React from 'react'
import View from '../../../../../components/pdf/View'
import Text from '../../../../../components/pdf/Text'
import Line from '../../../../../components/pdf/Line'
import Separator from '../../../../../components/pdf/Separator'
import { Table, TableCol, TableRow } from '../../../../../components/pdf/Table'
import Grid, { Col } from '../../../../../components/pdf/Grid'

import Icon from '../../../../../components/shapes/Icon'
import { useServiceOrder } from '../../_load/ServiceOrderLoader'
import {
  filterRepliesByParentReference,
  findQuestionById,
  findReplyByQuestionId,
  getDifferenceBetweenArrays,
  getRepliesFromBlockQuestion,
  getReplyValue,
  toCurrency
} from '../../_helpers/pdfHelpers'
import { formatDate } from '@btime/helpers'
import CoverSection from './sections/CoverSection'
import ContratoSection from './sections/ContratoSection'
import TeamSection from './sections/TeamSection'
import { useParentServiceOrder } from '../../_load/ParentServiceOrderLoader'

const PropostaTemplate = ({ templateId }) => {
  const { replies, checklist, name: clientName } = useParentServiceOrder()
  const { replies: childrenReplies } = useServiceOrder()

  const isProposal = templateId === 20
  const isContract = templateId === 22

  const meetingRecordingLink = findReplyByQuestionId(replies, 2223)?.value
  const featuresIncludedReplies = findReplyByQuestionId(replies, 2051)?.value
  const featuresIncludedQuestions = findQuestionById(checklist?.questions, 2051)?.options
  const featuresIncludedDifference = getDifferenceBetweenArrays(featuresIncludedQuestions, featuresIncludedReplies)

  const extraServicesReplies = findReplyByQuestionId(replies, 2235)?.value ?? [
    { name: 'Nenhum serviço extra contratado' }
  ]
  const extraServicesQuestions = findQuestionById(checklist?.questions, 2235)?.options ?? []
  const extraServicesDifference = getDifferenceBetweenArrays(extraServicesQuestions, extraServicesReplies)

  const expirationDate = findReplyByQuestionId(replies, 2056)?.value
  const formattedExpirationDate = formatDate('DD/MM/YYYY', expirationDate)

  const hasCustomDocs = findReplyByQuestionId(replies, 2224)?.value
  const hasIntegration = findReplyByQuestionId(replies, 2064)?.value
  const hasDashboard = findReplyByQuestionId(replies, 2240)?.value
  const hasExtraCustomDocs = findReplyByQuestionId(replies, 2099)?.value
  const hasSupport = findReplyByQuestionId(replies, 2070)?.value

  const paymentMethod = getReplyValue(findReplyByQuestionId(replies, 2305))?.name

  const contractDeploymentPaymentMethod = getReplyValue(findReplyByQuestionId(childrenReplies, 2079))?.name
  const contractDeploymentPaymentConditions = getReplyValue(findReplyByQuestionId(childrenReplies, 2080))?.name

  const contractMonthlyPaymentMethod = getReplyValue(findReplyByQuestionId(childrenReplies, 2082))?.name

  const razaoSocial = getReplyValue(findReplyByQuestionId(childrenReplies, 2076))
  const cnpj = getReplyValue(findReplyByQuestionId(childrenReplies, 2077))
  const address = getReplyValue(findReplyByQuestionId(childrenReplies, 2078))?.search
  const complement = getReplyValue(findReplyByQuestionId(childrenReplies, 2078))?.complement || null

  const clientInfo = { razaoSocial, cnpj, address, complement }

  const getPaymentMethodAndValue = (card, billet) => {
    return paymentMethod === 'Cartão'
      ? `${toCurrency(card)} ${isProposal ? '(Cartão)' : ''}`
      : `${toCurrency(billet)} ${isProposal ? '(Boleto)' : ''}`
  }

  const getAmountByPaymentMethod = (card, billet) => {
    return paymentMethod === 'Cartão' ? card : billet
  }

  const tacticsBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 2234)
    const tacticsReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const tacticsResult = {}
    tacticsReplies.forEach(tacticsReply => {
      const blockIndex = tacticsReply.blockIndex
      let tacticsData = tacticsResult[blockIndex]
      if (!tacticsData) {
        const blockReplies = getRepliesFromBlockQuestion(tacticsReplies, blockIndex)
        tacticsData = {
          item: getReplyValue(findReplyByQuestionId(blockReplies, 2212))
        }
        tacticsResult[blockIndex] = tacticsData
      }
    })
    return Object.values(tacticsResult)
  }

  const deploymentBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 2052)
    const deploymentReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)
    const logicReply = findReplyByQuestionId(replies, 2106)
    const optionsReply = filterRepliesByParentReference(replies, logicReply?.question?.reference)

    const deploymentResult = {}
    deploymentReplies.forEach(deploymentReply => {
      const blockIndex = deploymentReply.blockIndex
      let deploymentData = deploymentResult[blockIndex]
      if (!deploymentData) {
        const blockReplies = getRepliesFromBlockQuestion(deploymentReplies, blockIndex)
        const childReply = optionsReply.filter(reply => reply.blockIndex === blockIndex)[0]

        deploymentData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 2236))?.name,
          type: 'Pacote de implantação',
          meetings: getReplyValue(findReplyByQuestionId(blockReplies, 2053)),
          meetingTime: getReplyValue(findReplyByQuestionId(blockReplies, 2054))?.name,
          amount: getReplyValue(findReplyByQuestionId(blockReplies, 2055)) || 0,
          hasDiscount: getReplyValue(findReplyByQuestionId(blockReplies, 2106)),
          selectedPlan: getReplyValue(findReplyByQuestionId(blockReplies, 2057)),
          amountWithoutDiscount: getReplyValue(childReply) || 0
        }
        deploymentResult[blockIndex] = deploymentData
      }
    })
    return Object.values(deploymentResult)
  }

  const customDocBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 2237)
    const customDocReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const customDocResult = {}
    customDocReplies.forEach(customDocReply => {
      const blockIndex = customDocReply.blockIndex
      let customDocData = customDocResult[blockIndex]
      if (!customDocData) {
        const blockReplies = getRepliesFromBlockQuestion(customDocReplies, blockIndex)
        customDocData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 2238)),
          type: 'Documento customizado',
          description: getReplyValue(findReplyByQuestionId(blockReplies, 2239)),
          hours: getReplyValue(findReplyByQuestionId(blockReplies, 2225)),
          amount: getReplyValue(findReplyByQuestionId(blockReplies, 2226)) || 0,
          hasDiscount: getReplyValue(findReplyByQuestionId(blockReplies, 2227)),
          amountWithoutDiscount: getReplyValue(findReplyByQuestionId(replies, 2228)) || 0
        }
        customDocResult[blockIndex] = customDocData
      }
    })
    return Object.values(customDocResult)
  }

  const integrationBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 2094)
    const integrationReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)
    const logicReply = findReplyByQuestionId(replies, 2108)
    const optionsReply = filterRepliesByParentReference(replies, logicReply?.question?.reference)

    const integrationResult = {}
    integrationReplies.forEach(integrationReply => {
      const blockIndex = integrationReply.blockIndex
      let integrationData = integrationResult[blockIndex]
      if (!integrationData) {
        const blockReplies = getRepliesFromBlockQuestion(integrationReplies, blockIndex)
        const childReply = optionsReply.filter(reply => reply.blockIndex === blockIndex)[0]

        integrationData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 2095)),
          type: 'Integração',
          description: getReplyValue(findReplyByQuestionId(blockReplies, 2096)),
          hours: getReplyValue(findReplyByQuestionId(blockReplies, 2097)),
          amount: getReplyValue(findReplyByQuestionId(blockReplies, 2098)) || 0,
          hasDiscount: getReplyValue(findReplyByQuestionId(blockReplies, 2108)),
          amountWithoutDiscount: getReplyValue(childReply) || 0
        }
        integrationResult[blockIndex] = integrationData
      }
    })
    return Object.values(integrationResult)
  }

  const dashboardBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 2241)
    const dashboardReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)
    const logicReply = findReplyByQuestionId(replies, 2246)
    const optionsReply = filterRepliesByParentReference(replies, logicReply?.question?.reference)

    const dashboardResult = {}
    dashboardReplies.forEach(dashboardReply => {
      const blockIndex = dashboardReply.blockIndex
      let dashboardData = dashboardResult[blockIndex]
      if (!dashboardData) {
        const blockReplies = getRepliesFromBlockQuestion(dashboardReplies, blockIndex)
        const childReply = optionsReply.filter(reply => reply.blockIndex === blockIndex)[0]

        dashboardData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 2242)),
          type: 'Dashboard customizado',
          description: getReplyValue(findReplyByQuestionId(blockReplies, 2243)),
          hours: getReplyValue(findReplyByQuestionId(blockReplies, 2244)),
          amount: getReplyValue(findReplyByQuestionId(blockReplies, 2245)) || 0,
          hasDiscount: getReplyValue(findReplyByQuestionId(blockReplies, 2246)),
          amountWithoutDiscount: getReplyValue(childReply) || 0
        }
        dashboardResult[blockIndex] = dashboardData
      }
    })
    return Object.values(dashboardResult)
  }

  const extraCustomDocBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 2101)
    const extraCustomDocReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)
    const logicReply = findReplyByQuestionId(replies, 2112)
    const optionsReply = filterRepliesByParentReference(replies, logicReply?.question?.reference)

    const extraCustomDocResult = {}
    extraCustomDocReplies.forEach(extraCustomDocReply => {
      const blockIndex = extraCustomDocReply.blockIndex
      let extraCustomDocData = extraCustomDocResult[blockIndex]
      if (!extraCustomDocData) {
        const blockReplies = getRepliesFromBlockQuestion(extraCustomDocReplies, blockIndex)
        const childReply = optionsReply.filter(reply => reply.blockIndex === blockIndex)[0]

        extraCustomDocData = {
          type: 'Geração de Documentos',
          description: 'Documentos/Mês',
          quantity: getReplyValue(findReplyByQuestionId(blockReplies, 2102)),
          amountCard: getReplyValue(findReplyByQuestionId(blockReplies, 2103)) || 0,
          amountBillet: getReplyValue(findReplyByQuestionId(blockReplies, 2104)) || 0,
          selectedPlan: getReplyValue(findReplyByQuestionId(blockReplies, 2105)),
          hasDiscount: getReplyValue(findReplyByQuestionId(blockReplies, 2112)),
          amountWithoutDiscount: getReplyValue(childReply) || 0
        }
        extraCustomDocResult[blockIndex] = extraCustomDocData
      }
    })
    return Object.values(extraCustomDocResult)
  }

  const supportBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 2071)
    const supportReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)
    const logicReply = findReplyByQuestionId(replies, 2114)
    const optionsReply = filterRepliesByParentReference(replies, logicReply?.question?.reference)

    const supportResult = {}
    supportReplies.forEach(supportReply => {
      const blockIndex = supportReply.blockIndex
      let supportData = supportResult[blockIndex]
      if (!supportData) {
        const blockReplies = getRepliesFromBlockQuestion(supportReplies, blockIndex)
        const childReply = optionsReply.filter(reply => reply.blockIndex === blockIndex)[0]

        supportData = {
          type: 'Pacote de Sustentação',
          description: 'Horas/Mês',
          quantity: getReplyValue(findReplyByQuestionId(blockReplies, 2072)),
          amountCard: getReplyValue(findReplyByQuestionId(blockReplies, 2073)) || 0,
          amountBillet: getReplyValue(findReplyByQuestionId(blockReplies, 2074)) || 0,
          selectedPlan: getReplyValue(findReplyByQuestionId(blockReplies, 2075)),
          hasDiscount: getReplyValue(findReplyByQuestionId(blockReplies, 2114)),
          amountWithoutDiscount: getReplyValue(childReply) || 0
        }
        supportResult[blockIndex] = supportData
      }
    })
    return Object.values(supportResult)
  }

  const userPlanBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 2059)
    const userPlanReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const userPlanResult = {}
    userPlanReplies.forEach(userPlanReply => {
      const blockIndex = userPlanReply.blockIndex
      let userPlanData = userPlanResult[blockIndex]
      if (!userPlanData) {
        const blockReplies = getRepliesFromBlockQuestion(userPlanReplies, blockIndex)
        userPlanData = {
          type: 'Pacote base mensal',
          description: 'Usuário/Mês',
          quantity: getReplyValue(findReplyByQuestionId(blockReplies, 2060)),
          amountCard: getReplyValue(findReplyByQuestionId(blockReplies, 2061)) || 0,
          amountBillet: getReplyValue(findReplyByQuestionId(blockReplies, 2062)) || 0,
          selectedPlan: getReplyValue(findReplyByQuestionId(blockReplies, 2063)),
          hasDiscount: getReplyValue(findReplyByQuestionId(blockReplies, 2110)),
          amountWithoutDiscount: getReplyValue(findReplyByQuestionId(replies, 2111)) || 0
        }
        userPlanResult[blockIndex] = userPlanData
      }
    })
    return Object.values(userPlanResult)
  }

  const placePlanBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 2248)
    const placePlanReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const placePlanResult = {}
    placePlanReplies.forEach(placePlanReply => {
      const blockIndex = placePlanReply.blockIndex
      let placePlanData = placePlanResult[blockIndex]
      if (!placePlanData) {
        const blockReplies = getRepliesFromBlockQuestion(placePlanReplies, blockIndex)
        placePlanData = {
          type: 'Pacote base mensal',
          description: 'Local/Mês',
          quantity: getReplyValue(findReplyByQuestionId(blockReplies, 2249)),
          amountCard: getReplyValue(findReplyByQuestionId(blockReplies, 2250)) || 0,
          amountBillet: getReplyValue(findReplyByQuestionId(blockReplies, 2251)) || 0,
          selectedPlan: getReplyValue(findReplyByQuestionId(blockReplies, 2252)),
          hasDiscount: getReplyValue(findReplyByQuestionId(blockReplies, 2253)),
          amountWithoutDiscount: getReplyValue(findReplyByQuestionId(replies, 2254)) || 0
        }
        placePlanResult[blockIndex] = placePlanData
      }
    })
    return Object.values(placePlanResult)
  }

  const serviceOrderPlanBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 2255)
    const serviceOrderPlanReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const serviceOrderPlanResult = {}
    serviceOrderPlanReplies.forEach(serviceOrderPlanReply => {
      const blockIndex = serviceOrderPlanReply.blockIndex
      let serviceOrderPlanData = serviceOrderPlanResult[blockIndex]
      if (!serviceOrderPlanData) {
        const blockReplies = getRepliesFromBlockQuestion(serviceOrderPlanReplies, blockIndex)
        serviceOrderPlanData = {
          type: 'Pacote base mensal',
          description: 'Atividade/Mês',
          quantity: getReplyValue(findReplyByQuestionId(blockReplies, 2256)),
          amountCard: getReplyValue(findReplyByQuestionId(blockReplies, 2257)) || 0,
          amountBillet: getReplyValue(findReplyByQuestionId(blockReplies, 2258)) || 0,
          selectedPlan: getReplyValue(findReplyByQuestionId(blockReplies, 2259)),
          hasDiscount: getReplyValue(findReplyByQuestionId(blockReplies, 2260)),
          amountWithoutDiscount: getReplyValue(findReplyByQuestionId(replies, 2261)) || 0
        }
        serviceOrderPlanResult[blockIndex] = serviceOrderPlanData
      }
    })
    return Object.values(serviceOrderPlanResult)
  }

  const deploymentClientTeamBlock = () => {
    const blockReply = findReplyByQuestionId(childrenReplies, 2086)
    const deploymentClientTeamReplies = filterRepliesByParentReference(childrenReplies, blockReply?.question?.reference)

    const deploymentClientTeamResult = {}
    deploymentClientTeamReplies.forEach(deploymentClientTeamReply => {
      const blockIndex = deploymentClientTeamReply.blockIndex
      let deploymentClientTeamData = deploymentClientTeamResult[blockIndex]
      if (!deploymentClientTeamData) {
        const blockReplies = getRepliesFromBlockQuestion(deploymentClientTeamReplies, blockIndex)
        deploymentClientTeamData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 2087)),
          phone: getReplyValue(findReplyByQuestionId(blockReplies, 2088)),
          email: getReplyValue(findReplyByQuestionId(blockReplies, 2089))
        }
        deploymentClientTeamResult[blockIndex] = deploymentClientTeamData
      }
    })
    return Object.values(deploymentClientTeamResult)
  }

  const financialClientTeamBlock = () => {
    const blockReply = findReplyByQuestionId(childrenReplies, 2090)
    const financialClientTeamReplies = filterRepliesByParentReference(childrenReplies, blockReply?.question?.reference)

    const financialClientTeamResult = {}
    financialClientTeamReplies.forEach(financialClientTeamReply => {
      const blockIndex = financialClientTeamReply.blockIndex
      let financialClientTeamData = financialClientTeamResult[blockIndex]
      if (!financialClientTeamData) {
        const blockReplies = getRepliesFromBlockQuestion(financialClientTeamReplies, blockIndex)
        financialClientTeamData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 2091)),
          phone: getReplyValue(findReplyByQuestionId(blockReplies, 2092)),
          email: getReplyValue(findReplyByQuestionId(blockReplies, 2093))
        }
        financialClientTeamResult[blockIndex] = financialClientTeamData
      }
    })
    return Object.values(financialClientTeamResult)
  }

  const tacticsReplies = tacticsBlock()
  const deploymentReplies = deploymentBlock()
  const customDocReplies = customDocBlock()
  const integrationReplies = integrationBlock()
  const dashboardReplies = dashboardBlock()
  const extraCustomDocReplies = extraCustomDocBlock()
  const supportReplies = supportBlock()
  const userPlanReplies = userPlanBlock()
  const placePlanReplies = placePlanBlock()
  const serviceOrderPlanReplies = serviceOrderPlanBlock()

  const deploymentClientTeamReplies = deploymentClientTeamBlock()
  const financialClientTeamReplies = financialClientTeamBlock()

  const deploymentSelectPlan = deploymentReplies.filter(reply => reply.selectedPlan === true)

  const userSelectPlan = userPlanReplies.filter(reply => reply.selectedPlan === true)
  const placeSelectPlan = placePlanReplies.filter(reply => reply.selectedPlan === true)
  const serviceOrderSelectPlan = serviceOrderPlanReplies.filter(reply => reply.selectedPlan === true)
  const extraCustomDocSelectPlan = extraCustomDocReplies.filter(reply => reply.selectedPlan === true)
  const supportSelectPlan = supportReplies.filter(reply => reply.selectedPlan === true)

  const deploymentResume = [...deploymentSelectPlan, ...dashboardReplies, ...integrationReplies, ...customDocReplies]

  const monthlyPaymentResume = [
    ...userSelectPlan,
    ...placeSelectPlan,
    ...serviceOrderSelectPlan,
    ...extraCustomDocSelectPlan,
    ...supportSelectPlan
  ]

  const monthlyPaymentCard = monthlyPaymentResume.reduce((total, service) => total + service.amountCard, 0)
  const monthlyPaymentBillet = monthlyPaymentResume.reduce((total, service) => total + service.amountBillet, 0)
  const deploymentPayment = deploymentResume.reduce((total, service) => total + service.amount, 0)

  const deploymentPaymentWithoutDiscount = deploymentResume.reduce((total, service) => {
    if (service.hasDiscount) {
      return total + service.amountWithoutDiscount
    } else {
      return total + service.amount
    }
  }, 0)

  const monthlyPaymentWithoutDiscount = monthlyPaymentResume.reduce((total, service) => {
    if (service.hasDiscount) {
      return total + service.amountWithoutDiscount
    } else {
      if (paymentMethod === 'Cartão') {
        return total + service.amountCard
      } else {
        return total + service.amountBillet
      }
    }
  }, 0)

  return (
    <View pageBreakAfter>
      <CoverSection clientName={clientName} isContract={isContract} />
      <div id="escopo-projeto">
        <Text semibold size="h3">
          Escopo do Projeto
        </Text>
        <Line />
        <Separator weight={3} />
        <Grid style={{ gap: 5 }}>
          <Text semibold inline>
            📹 Link da Gravação da Reunião:
          </Text>
          <a
            href={meetingRecordingLink}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              textDecoration: 'none',
              width: 'fit-content'
            }}
          >
            <Text inline> {meetingRecordingLink}</Text>
            <Icon name="BsLink45Deg" size="1rem" color="#000000" />
          </a>
        </Grid>
        <Separator weight={10} />
        <div id="escopo-projeto-1">
          <Text semibold size="h5">
            🙋 Quais são as dores do cliente e como resolveremos elas?
          </Text>
          <Separator weight={3} />
          <Table width="100%" color="#333333">
            <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
              <TableCol width="50%">
                <Text semibold>Sem btime</Text>
              </TableCol>
              <TableCol color="#333333">
                <Text semibold>Com btime</Text>
              </TableCol>
            </TableRow>
            <TableRow color="#333333">
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Processo no papel:</b> Todo registro de informações da operação é feita no papel, o que gera muita
                  perda de dados e inconsistência de informações.
                </Text>
              </TableCol>
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Digitalização:</b> A btime digitaliza toda a coleta de dados e garante padronização de dados e
                  geração de relatórios em tempo real.
                </Text>
              </TableCol>
            </TableRow>
            <TableRow color="#333333">
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Dificuldade na gestão:</b> Com os dados despadronizados e em papel, você não consegue fazer uma
                  gestão eficiente dos seus processos e não consegue saber o que está acontecendo na sua operação.
                </Text>
              </TableCol>
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Gestão em tempo real:</b> Com o seu processos digitalizados, você consegue fazer a gestão da sua
                  operação completa em um só lugar, acompanhando a evolução de cada etapa em tempo real.
                </Text>
              </TableCol>
            </TableRow>
            <TableRow color="#333333">
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Falta de Relatórios:</b> Sem padronização e centralização dos dados, você não consegue ter
                  relatórios e dashboards para auxiliar na tomada de decisão, te deixando sem visibilidade do que está
                  acontecendo.
                </Text>
              </TableCol>
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Relatórios Automáticos:</b> Geramos relatórios e dashboards automáticos que te municiam de
                  informações estratégias para que sua tomada de decisão seja precisa e ágil.
                </Text>
              </TableCol>
            </TableRow>
            <TableRow color="#333333">
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Trabalho Manual:</b> Você e sua equipe perdem horas e horas em trabalhos manuais e operacionais,
                  reescrevendo dados do papel, caçando histórico em conversas do WhatsApp e não sobra tempo para fazer o
                  trabalho que realmente importa.
                </Text>
              </TableCol>
              <TableCol color="#333333">
                <Text align="justify">
                  <b>Automações:</b> Automatizamos todos os seus trabalhos manuais e operacionais, não só coletando
                  dados, gerando relatórios e dashboards instantaneamente, mas também automatizando ações e análises que
                  hoje são feito por pessoas, liberando tempo para você e sua equipe focaram no seu negócio.
                </Text>
              </TableCol>
            </TableRow>
          </Table>
        </div>
        <Separator weight={10} />

        <div id="escopo-projeto-2">
          <Text semibold size="h5">
            🚀 Em quais processos a btime vai atuar?
          </Text>
          <Separator weight={3} />
          <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
            {tacticsReplies.map((reply, index) => {
              return (
                <li key={index}>
                  <Text> {reply.item}</Text>
                </li>
              )
            })}
          </ul>
        </div>
        <Separator weight={10} />

        <div id="escopo-projeto-3">
          <Text semibold size="h5">
            🧑‍💻 Quais são as funcionalidades inclusas no seu plano?
          </Text>
          <Separator weight={3} />

          <Grid direction="row" align="start" style={{ gap: 10 }}>
            <Col width="50%" gutter={10} style={{ background: '#dcfce7', borderRadius: 10 }}>
              <Text semibold>✅ Incluso:</Text>
              <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
                {featuresIncludedReplies.map((reply, index) => {
                  return (
                    <li key={index}>
                      <Text align="justify">{reply.name}</Text>
                    </li>
                  )
                })}
              </ul>
            </Col>
            <Col width="50%" gutter={10} style={{ background: '#fee2e2', borderRadius: 10 }}>
              <Text semibold>🔴 Não Incluso:</Text>
              <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
                {featuresIncludedDifference.map((reply, index) => {
                  return (
                    <li key={index}>
                      <Text align="justify">{reply.name}</Text>
                    </li>
                  )
                })}
              </ul>
            </Col>
          </Grid>
        </div>
        <Separator weight={10} />

        <div id="escopo-projeto-4">
          <Text semibold size="h5">
            🧑‍💻 Quais são os serviços extras inclusos no seu plano? (Serviços contratados à parte)
          </Text>
          <Separator weight={3} />

          <Grid direction="row" align="start" style={{ gap: 10 }}>
            <Col width="50%" gutter={10} style={{ background: '#dcfce7', borderRadius: 10 }}>
              <Text semibold>✅ Incluso:</Text>
              <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
                {extraServicesReplies.map((reply, index) => {
                  return (
                    <li key={index}>
                      <Text align="justify">{reply.name}</Text>
                    </li>
                  )
                })}
              </ul>
            </Col>
            <Col width="50%" gutter={10} style={{ background: '#fee2e2', borderRadius: 10 }}>
              <Text semibold>🔴 Não Incluso:</Text>
              <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
                {extraServicesDifference.map((reply, index) => {
                  return (
                    <li key={index}>
                      <Text align="justify">{reply.name}</Text>
                    </li>
                  )
                })}
              </ul>
            </Col>
          </Grid>
        </div>
      </div>
      <Separator weight={15} />

      {isContract && (
        <TeamSection
          clientName={clientName}
          deploymentClientTeamReplies={deploymentClientTeamReplies}
          financialClientTeamReplies={financialClientTeamReplies}
        />
      )}

      <div id="resumo-investimento">
        <Text semibold size="h3">
          Resumo do Investimento
        </Text>
        <Line />
        <Separator weight={3} />
        <Text align="justify">
          Resumo dos pacotes completos de Implantação e Mensalidade. Descrição completa de cada pacote nas seções
          seguintes.
        </Text>
        <Separator weight={5} />
        <div id="resumo-investimento-1">
          <Text semibold size="h5">
            Implantação - {toCurrency(deploymentPayment)} {}
            {isProposal && deploymentPaymentWithoutDiscount !== deploymentPayment && (
              <s>(Preço Original: {toCurrency(deploymentPaymentWithoutDiscount)})</s>
            )}
          </Text>
          <Text>Itens envolvidos na implantação (Detalhamento na sessão seguinte)</Text>
          <Separator weight={3} />
          <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
            {deploymentResume.map((item, index) => {
              return (
                <li key={index}>
                  <Text>
                    {item.type}: {item.name} - {toCurrency(item.amount)}{' '}
                    {item.hasDiscount && isProposal && (
                      <s>(Preço Original: {toCurrency(item.amountWithoutDiscount)})</s>
                    )}
                  </Text>
                </li>
              )
            })}
          </ul>
          {isContract && (
            <>
              <Text>
                <b>Meio de Pagamento</b>: {contractDeploymentPaymentMethod}
              </Text>
              <Text>
                <b>Condições de Pagamento</b>: {contractDeploymentPaymentConditions}
              </Text>
            </>
          )}
        </div>

        <Separator weight={10} />

        <div id="resumo-investimento-2">
          <Text semibold size="h5">
            Mensalidade - {getPaymentMethodAndValue(monthlyPaymentCard, monthlyPaymentBillet)}{' '}
            {isProposal &&
              getAmountByPaymentMethod(monthlyPaymentCard, monthlyPaymentBillet) !== monthlyPaymentWithoutDiscount && (
                <s>(Preço Original: {toCurrency(monthlyPaymentWithoutDiscount)})</s>
              )}
          </Text>
          <Text>Itens envolvidos no pacote de cobrança mensal (Detalhamento na sessão seguinte)</Text>
          <Separator weight={3} />
          <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
            {monthlyPaymentResume.map((item, index) => {
              return (
                <li key={index}>
                  <Text>
                    {item.type}: {item.quantity} {item.description} -{' '}
                    {getPaymentMethodAndValue(item.amountCard, item.amountBillet)}{' '}
                    {item.hasDiscount && isProposal && (
                      <s>(Preço Original: {toCurrency(item.amountWithoutDiscount)})</s>
                    )}
                  </Text>
                </li>
              )
            })}
          </ul>
          {isContract && (
            <>
              <Text>
                <b>Meio de Pagamento</b>: {contractMonthlyPaymentMethod}
              </Text>
            </>
          )}
        </div>

        <Separator weight={10} />

        <Text semibold bgColor="#fef9c3" style={{ padding: '10px 15px', borderRadius: 5 }}>
          ⚠️<i> Condições válidas até dia {formattedExpirationDate}</i>
        </Text>
      </div>

      <Separator weight={15} />

      <div id="detalhes-implantacao">
        <Text semibold size="h3">
          Detalhes da Implantação
        </Text>
        <Line />
        <Separator weight={5} />
        <div id="detalhes-implantacao-1">
          <Text semibold size="h5">
            O que é a implantação e como ela funciona?
          </Text>
          <Separator weight={2} />
          <Text align="justify">
            A implantação é o processo de criação e preparação de seu ambiente dedicado da btime, junto com o
            treinamento de sua equipe no uso da ferramenta. Nosso processo consiste em reuniões de até 60 minutos onde
            orientaremos a equipe responsável em como configurar a plataforma, realizar os cadastros e usá-la no seu dia
            a dia.
          </Text>
          <Separator weight={2} />

          <Text align="justify">
            O processo é feito por um especialista da btime que vai setFocus em capacitar os evolvidos no uso da
            plataforma, formando replicadores desse conhecimento internamente. Nosso objetivo é guiá-los de como usar a
            plataforma para que tenham o poder de realizar as configurações e cadastros por conta própria. Criando
            materiais de treinamento à partir das reuniões gravadas e de materiais escritos.
          </Text>
          <Separator weight={2} />
          <Text align="justify">
            Seguimos um modelo padrão de etapas, que são detalhados durante a reunião inicial, assim como a criação de
            um cronograma de até 4 semanas. As etapas são:
          </Text>
          <Separator weight={2} />
          <ul style={{ margin: '8px 0 14px', paddingLeft: '20px' }}>
            <li>
              <Text>Reunião de Kickoff;</Text>
            </li>
            <li>
              <Text>Treinamento de Configurações;</Text>
            </li>
            <li>
              <Text>Treinamento de Cadastros;</Text>
            </li>
            <li>
              <Text>Treinamento do Execução (Web e App);</Text>
            </li>
            <li>
              <Text>Rollout.</Text>
            </li>
          </ul>

          <Separator weight={2} />
          <Text bgColor="#fef9c3" style={{ padding: '10px 15px', borderRadius: 5 }}>
            💡{' '}
            <i>
              Os escopos extras, caso existentes, de integração e criação de documentos customizados terão um cronograma
              à parte, combinado durante as respectivas reuniões de alinhamento.
            </i>
          </Text>
        </div>

        <Separator weight={10} />

        <div id="detalhes-implantacao-2">
          <Text semibold size="h5">
            Detalhamento dos valores de implantação
          </Text>
          <Separator weight={2} />

          <Text align="justify">
            O pacote selecionado do seu plano está destacado em{' '}
            <Text inline bgColor="#dcfce7" style={{ padding: '5px', borderRadius: 5, lineHeight: 0.75 }}>
              verde
            </Text>
            . Novos pacotes podem ser contratados após o termino da implantação inicial, seguindo os valores da tabela a
            seguir:
          </Text>
          <Separator weight={5} />

          <Table width="100%" color="#333333">
            <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
              <TableCol color="#333333" width="40%">
                <Text semibold>Plano</Text>
              </TableCol>
              <TableCol color="#333333">
                <Text semibold>Encontros</Text>
              </TableCol>
              <TableCol color="#333333">
                <Text semibold>Horas Totais</Text>
              </TableCol>
              <TableCol color="#333333">
                <Text semibold>Valor Total</Text>
              </TableCol>
            </TableRow>
            {deploymentReplies.map((reply, index) => {
              return (
                <TableRow color="#333333" key={index} bgColor={reply.selectedPlan ? '#dcfce7' : ''}>
                  <TableCol color="#333333">
                    <Text>{reply.name}</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text>{reply.meetings} reuniões</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text>{reply.meetingTime}</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text>{toCurrency(reply.amount)}</Text>
                  </TableCol>
                </TableRow>
              )
            })}
          </Table>
        </div>

        {hasCustomDocs && (
          <>
            <Separator weight={10} />
            <div id="detalhes-implantacao-3">
              <Text semibold size="h5">
                Detalhamento dos valores da criação de Documentos Customizados
              </Text>
              <Separator weight={2} />

              <Text align="justify">
                Pacote de horas necessário para a criação de cada um dos documentos customizados mapeados da sua
                operação, e a descrição do escopo de cada um desses documentos.
              </Text>
              <Separator weight={5} />

              <Table width="100%" color="#333333">
                <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                  <TableCol color="#333333" width="40%">
                    <Text semibold>Documento</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Descrição</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total de Horas</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Valor Total</Text>
                  </TableCol>
                </TableRow>
                {customDocReplies.map((reply, index) => {
                  return (
                    <TableRow color="#333333" key={index}>
                      <TableCol color="#333333">
                        <Text>{reply.name}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.description}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.hours}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{toCurrency(reply.amount)}</Text>
                      </TableCol>
                    </TableRow>
                  )
                })}
              </Table>
            </div>
          </>
        )}

        {hasIntegration && (
          <>
            <Separator weight={10} />
            <div id="detalhes-implantacao-4">
              <Text semibold size="h5">
                Detalhamento dos valores de Integração
              </Text>
              <Separator weight={2} />

              <Text align="justify">
                Pacote de horas necessário para o desenvolvimento de cada uma das integrações, e a descrição do escopo
                de cada uma delas.
              </Text>
              <Separator weight={5} />

              <Table width="100%" color="#333333">
                <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                  <TableCol color="#333333" width="40%">
                    <Text semibold>Sistema</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Descrição</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total de Horas</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Valor Total</Text>
                  </TableCol>
                </TableRow>
                {integrationReplies.map((reply, index) => {
                  return (
                    <TableRow color="#333333" key={index}>
                      <TableCol color="#333333">
                        <Text>{reply.name}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.description}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.hours}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{toCurrency(reply.amount)}</Text>
                      </TableCol>
                    </TableRow>
                  )
                })}
              </Table>
            </div>
          </>
        )}

        {hasDashboard && (
          <>
            <Separator weight={10} />
            <div id="detalhes-implantacao-5">
              <Text semibold size="h5">
                Detalhamento dos valores da criação de Dashboards Customizados
              </Text>
              <Separator weight={2} />

              <Text align="justify">
                Pacote de horas necessário para a criação e desenvolvimento de cada um dos dashboards customizados
                mapeados da sua operação, e a descrição do escopo de cada um deles.
              </Text>
              <Separator weight={5} />

              <Table width="100%" color="#333333">
                <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                  <TableCol color="#333333" width="40%">
                    <Text semibold>Dashboard</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Descrição</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total de Horas</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Valor Total</Text>
                  </TableCol>
                </TableRow>
                {dashboardReplies.map((reply, index) => {
                  return (
                    <TableRow color="#333333" key={index}>
                      <TableCol color="#333333">
                        <Text>{reply.name}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.description}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{reply.hours}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{toCurrency(reply.amount)}</Text>
                      </TableCol>
                    </TableRow>
                  )
                })}
              </Table>
            </div>
          </>
        )}
      </div>

      <Separator weight={15} />

      <div id="detalhes-mensalidade">
        <Text semibold size="h3">
          Detalhes da Mensalidade - Plataforma btime
        </Text>
        <Line />

        <Separator weight={5} />

        <div id="detalhes-mensalidade-1">
          <Text semibold size="h5">
            O que é a mensalidade e como ela funciona?
          </Text>
          <Separator weight={2} />
          <Text align="justify">
            A mensalidade - plataforma btime é o valor referente ao pacote de volume contratado mensalmente, que engloba
            o uso da plataforma e suporte. O valor é cobrado mensalmente pelo meio selecionado pelo cliente durante a
            contratação na data de vencimento.
          </Text>
        </div>

        <Separator weight={10} />

        <div id="detalhes-mensalidade-2">
          <Text semibold size="h5">
            Detalhamento do pacote Mensal
          </Text>
          <Separator weight={2} />
          <Text>Valor relacionado ao pacote mensal contratado.</Text>

          {!!userPlanReplies.length && (
            <>
              <Separator weight={3} />
              <Text size={12}>
                <i>
                  Plano selecionado destacado em{' '}
                  <Text
                    inline
                    bgColor="#dcfce7"
                    style={{ padding: '5px', borderRadius: 5, lineHeight: 0.75 }}
                    size={12}
                  >
                    verde
                  </Text>
                </i>
              </Text>

              <Separator weight={2} />

              <Table width="100%" color="#333333">
                <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                  <TableCol color="#333333" width="33%">
                    <Text semibold>Usuários/Mês</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total (Cartão)</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total (Boleto)</Text>
                  </TableCol>
                </TableRow>
                {userPlanReplies.map((reply, index) => {
                  return (
                    <TableRow color="#333333" key={index} bgColor={reply.selectedPlan ? '#dcfce7' : ''}>
                      <TableCol color="#333333">
                        <Text>{reply.quantity}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{toCurrency(reply.amountCard)}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{toCurrency(reply.amountBillet)}</Text>
                      </TableCol>
                    </TableRow>
                  )
                })}
              </Table>
            </>
          )}

          {!!placePlanReplies.length && (
            <>
              <Separator weight={3} />
              <Text size={12}>
                <i>
                  Plano selecionado destacado em{' '}
                  <Text
                    inline
                    bgColor="#dcfce7"
                    style={{ padding: '5px', borderRadius: 5, lineHeight: 0.75 }}
                    size={12}
                  >
                    verde
                  </Text>
                </i>
              </Text>

              <Separator weight={2} />

              <Table width="100%" color="#333333">
                <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                  <TableCol color="#333333" width="33%">
                    <Text semibold>Locais/Mês</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total (Cartão)</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total (Boleto)</Text>
                  </TableCol>
                </TableRow>
                {placePlanReplies.map((reply, index) => {
                  return (
                    <TableRow color="#333333" key={index} bgColor={reply.selectedPlan ? '#dcfce7' : ''}>
                      <TableCol color="#333333">
                        <Text>{reply.quantity}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{toCurrency(reply.amountCard)}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{toCurrency(reply.amountBillet)}</Text>
                      </TableCol>
                    </TableRow>
                  )
                })}
              </Table>
            </>
          )}

          {!!serviceOrderPlanReplies.length && (
            <>
              <Separator weight={3} />
              <Text size={12}>
                <i>
                  Plano selecionado destacado em{' '}
                  <Text
                    inline
                    bgColor="#dcfce7"
                    style={{ padding: '5px', borderRadius: 5, lineHeight: 0.75 }}
                    size={12}
                  >
                    verde
                  </Text>
                </i>
              </Text>

              <Separator weight={2} />

              <Table width="100%" color="#333333">
                <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                  <TableCol color="#333333" width="33%">
                    <Text semibold>Atividades/Mês</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total (Cartão)</Text>
                  </TableCol>
                  <TableCol color="#333333">
                    <Text semibold>Total (Boleto)</Text>
                  </TableCol>
                </TableRow>
                {serviceOrderPlanReplies.map((reply, index) => {
                  return (
                    <TableRow color="#333333" key={index} bgColor={reply.selectedPlan ? '#dcfce7' : ''}>
                      <TableCol color="#333333">
                        <Text>{reply.quantity}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{toCurrency(reply.amountCard)}</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text>{toCurrency(reply.amountBillet)}</Text>
                      </TableCol>
                    </TableRow>
                  )
                })}
              </Table>
            </>
          )}
        </div>
      </div>

      {(hasExtraCustomDocs || hasSupport) && (
        <>
          <Separator weight={15} />

          <div id="detalhes-servicos-extras">
            <Text semibold size="h3">
              Detalhes da Mensalidade - Serviços Extras
            </Text>
            <Line />

            <Separator weight={5} />
            <div id="detalhes-servicos-extras-1">
              <Text semibold size="h5">
                O que é a mensalidade de serviços extras e como ela funciona?
              </Text>
              <Separator weight={2} />
              <Text align="justify">
                A mensalidade de serviços extras é o valor referente ao pacote de volume contratado mensalmente para
                funcionalidades do escopo extra, como geração de documentos customizados e pacote de sustentação. essas
                pacotes, apesar de apresentados separadamente aqui, serão cobrados junto a mensalidade da plataforma
                btime.
              </Text>
            </div>

            {hasExtraCustomDocs && (
              <>
                <Separator weight={5} />
                <div id="detalhes-servicos-extras-2">
                  <Text semibold size="h5">
                    Detalhamento do pacote de Geração de Documentos
                  </Text>
                  <Separator weight={2} />
                  <Text>Valor relacionado ao pacote mensal contratado.</Text>
                  <Separator weight={3} />
                  <Text size={12}>
                    <i>
                      Plano selecionado destacado em{' '}
                      <Text
                        inline
                        bgColor="#dcfce7"
                        style={{ padding: '5px', borderRadius: 5, lineHeight: 0.75 }}
                        size={12}
                      >
                        verde
                      </Text>
                    </i>
                  </Text>

                  <Separator weight={2} />

                  <Table width="100%" color="#333333">
                    <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                      <TableCol color="#333333" width="33%">
                        <Text semibold>Documentos/Mês</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text semibold>Total (Cartão)</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text semibold>Total (Boleto)</Text>
                      </TableCol>
                    </TableRow>
                    {extraCustomDocReplies.map((reply, index) => {
                      return (
                        <TableRow color="#333333" key={index} bgColor={reply.selectedPlan ? '#dcfce7' : ''}>
                          <TableCol color="#333333">
                            <Text>{reply.quantity}</Text>
                          </TableCol>
                          <TableCol color="#333333">
                            <Text>{toCurrency(reply.amountCard)}</Text>
                          </TableCol>
                          <TableCol color="#333333">
                            <Text>{toCurrency(reply.amountBillet)}</Text>
                          </TableCol>
                        </TableRow>
                      )
                    })}
                  </Table>
                </div>
              </>
            )}

            {hasSupport && (
              <>
                <Separator weight={5} />
                <div id="detalhes-servicos-extras-3">
                  <Text semibold size="h5">
                    Detalhamento do pacote de Sustentação
                  </Text>
                  <Separator weight={2} />
                  <Text>Valor relacionado ao pacote mensal de horas de Sustentação.</Text>
                  <Separator weight={3} />
                  <Text size={12}>
                    <i>
                      Plano selecionado destacado em{' '}
                      <Text
                        inline
                        bgColor="#dcfce7"
                        style={{ padding: '5px', borderRadius: 5, lineHeight: 0.75 }}
                        size={12}
                      >
                        verde
                      </Text>
                    </i>
                  </Text>
                  <Separator weight={2} />

                  <Table width="100%" color="#333333">
                    <TableRow color="#333333" style={{ background: '#f4f4f5' }}>
                      <TableCol color="#333333" width="33%">
                        <Text semibold>Horas/Mês</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text semibold>Total (Cartão)</Text>
                      </TableCol>
                      <TableCol color="#333333">
                        <Text semibold>Total (Boleto)</Text>
                      </TableCol>
                    </TableRow>
                    {supportReplies.map((reply, index) => {
                      return (
                        <TableRow color="#333333" key={index} bgColor={reply.selectedPlan ? '#dcfce7' : ''}>
                          <TableCol color="#333333">
                            <Text>{reply.quantity}</Text>
                          </TableCol>
                          <TableCol color="#333333">
                            <Text>{toCurrency(reply.amountCard)}</Text>
                          </TableCol>
                          <TableCol color="#333333">
                            <Text>{toCurrency(reply.amountBillet)}</Text>
                          </TableCol>
                        </TableRow>
                      )
                    })}
                  </Table>
                </div>
              </>
            )}
          </div>
        </>
      )}
      <Separator weight={15} />

      <div id="fluxo-contratacao">
        <Text semibold size="h3">
          Fluxo de Contratação
        </Text>
        <Line />
        <Separator weight={5} />
        <Text align="justify">
          Após a aceitação desta proposta, enviaremos um link com um formulário para coletar informações sobre sua
          empresa, que serão utilizadas para a elaboração do contrato. Este será enviado para assinatura digital.
        </Text>
        <Separator weight={2} />

        <Text align="justify">
          Uma nota fiscal com os valores referentes à implantação será emitida assim que o contrato for assinado.
        </Text>
        <Separator weight={2} />

        <Text align="justify">
          Com o pagamento inicial, o time de implantação da btime entrará em contato por e-mail, se apresentando e
          fornecendo um link com a disponibilidade de agenda do nosso time de implantação. Você poderá escolher a melhor
          data e horário que se adéque à sua disponibilidade, dando início ao processo de implantação.
        </Text>
        <Separator weight={2} />

        <Text semibold align="justify">
          Este processo pode durar até 4 semanas, conforme o nosso cronograma.
        </Text>
        <Separator weight={2} />

        <Text align="justify">
          Caso haja atraso no envio de dados por parte do cliente, a mensalidade será cobrada independentemente da
          situação da implantação. Se a implantação for concluída antes do prazo de 4 semanas, o boleto da mensalidade
          será enviado após o email de formalização do rollout do projeto.
        </Text>
        <Separator weight={2} />

        <Text align="justify">
          Mensalmente, o volume de serviços utilizado pelo cliente será avaliado, e um boleto correspondente ao uso será
          emitido para pagamento. Após o pagamento, a nota fiscal dos serviços prestados será enviada automaticamente.
        </Text>
      </div>

      <Separator weight={15} />

      <ContratoSection isContract={isContract} clientInfo={clientInfo} />
    </View>
  )
}

export default PropostaTemplate
